<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import ContactCardsAPI from 'dashboard/api/whatsappContactCards';
import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { id, contentAttributes } = useMessageContext();
const { t } = useI18n();
const route = useRoute();
const cards = computed(() => contentAttributes.value.whatsappContacts || []);
const saving = ref('');
const saved = ref({});
const phoneKey = (cardIndex, phoneIndex) => `${cardIndex}:${phoneIndex}`;
const canSave = (number) => /^\+[1-9][0-9]{1,14}$/.test(number);
const contactUrl = (contactId) =>
  `/app/accounts/${route.params.accountId}/contacts/${contactId}`;

async function saveContact(cardIndex, phoneIndex) {
  if (saving.value) return;
  const key = phoneKey(cardIndex, phoneIndex);
  saving.value = key;
  try {
    const response = await ContactCardsAPI.save(
      id.value,
      cardIndex,
      phoneIndex
    );
    saved.value[key] = response.data.id;
    useAlert(
      t(
        response.data.existing
          ? 'WHATSAPP_CONTACT_CARDS.EXISTS'
          : 'WHATSAPP_CONTACT_CARDS.SAVED'
      )
    );
  } catch {
    useAlert(t('WHATSAPP_CONTACT_CARDS.SAVE_ERROR'));
  } finally {
    saving.value = '';
  }
}
</script>

<template>
  <BaseBubble class="p-3" data-bubble-name="whatsapp-contacts">
    <div class="whatsapp-contact-cards grid min-w-0 w-60 max-w-full gap-3">
      <section
        v-for="(card, cardIndex) in cards"
        :key="cardIndex"
        class="grid gap-2 rounded-lg border border-n-weak p-3"
      >
        <div class="flex items-center gap-2">
          <span class="i-ph-address-book text-xl shrink-0" aria-hidden="true" />
          <span class="min-w-0 font-medium break-all" dir="auto">{{
            card.name || t('WHATSAPP_CONTACT_CARDS.CONTACT')
          }}</span>
        </div>
        <div
          v-for="(phone, phoneIndex) in card.phones"
          :key="phoneIndex"
          class="grid gap-1"
        >
          <span dir="ltr" class="text-sm break-all">{{ phone.number }}</span>
          <span v-if="phone.label" class="text-xs opacity-80">{{
            phone.label
          }}</span>
          <router-link
            v-if="saved[phoneKey(cardIndex, phoneIndex)]"
            :to="contactUrl(saved[phoneKey(cardIndex, phoneIndex)])"
            class="text-sm underline"
            >{{ t('WHATSAPP_CONTACT_CARDS.OPEN') }}</router-link
          >
          <NextButton
            v-else-if="canSave(phone.number)"
            :label="t('WHATSAPP_CONTACT_CARDS.SAVE')"
            :disabled="!!saving"
            :is-loading="saving === phoneKey(cardIndex, phoneIndex)"
            icon="i-ph-user-plus"
            sm
            faded
            slate
            @click="saveContact(cardIndex, phoneIndex)"
          />
          <span v-else class="text-xs opacity-80">{{
            t('WHATSAPP_CONTACT_CARDS.INTERNATIONAL_REQUIRED')
          }}</span>
        </div>
        <span v-if="!card.phones.length" class="text-sm opacity-80">{{
          t('WHATSAPP_CONTACT_CARDS.NO_PHONE')
        }}</span>
      </section>
    </div>
  </BaseBubble>
</template>
